import { useCallback, useRef } from "react";

import { getSmileSimulationFrameSize } from "../lib/image";
import type { SmileSimulationFrameSize } from "../model/types";

type UseSmileSimulationViewportOptions = {
  onFrameSizeChange: (frameSize: SmileSimulationFrameSize) => void;
};

export function useSmileSimulationViewport({
  onFrameSizeChange,
}: UseSmileSimulationViewportOptions) {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateCameraFrameSize = useCallback(
    (availableWidth: number, availableHeight: number) => {
      const nextSize = getSmileSimulationFrameSize(
        availableWidth,
        availableHeight,
      );

      if (nextSize.width <= 0 || nextSize.height <= 0) {
        return;
      }

      onFrameSizeChange(nextSize);
    },
    [onFrameSizeChange],
  );

  const cameraFrameViewportRef = useCallback(
    (cameraFrameViewport: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      if (!cameraFrameViewport) {
        return;
      }

      const { width, height } = cameraFrameViewport.getBoundingClientRect();
      updateCameraFrameSize(width, height);

      if (typeof ResizeObserver === "undefined") {
        return;
      }

      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        updateCameraFrameSize(
          entry.contentRect.width,
          entry.contentRect.height,
        );
      });

      resizeObserver.observe(cameraFrameViewport);
      resizeObserverRef.current = resizeObserver;
    },
    [updateCameraFrameSize],
  );

  return {
    cameraFrameViewportRef,
  };
}
